import { cx } from 'class-variance-authority'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import React, { forwardRef, createContext, useContext } from 'react'

import { Validation as SharedValidation } from '../Validation/Validation'

import styles from './InputField.module.scss'

export const InputFieldContext = createContext<{
  id: string
  error?: boolean
  disabled?: boolean
  readOnly?: boolean
  validationId?: string
  labelId?: string
  hasValidation: boolean
} | null>(null)

export interface RootProps {
  id: string
  children: ReactNode
  className?: string
  error?: boolean
  disabled?: boolean
  readOnly?: boolean
}

const Root = ({
  id,
  children,
  className,
  error,
  disabled,
  readOnly
}: RootProps) => {
  const validationId = `${id}-validation`
  const labelId = `${id}-label`

  const hasValidation = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === Validation
  )

  return (
    <InputFieldContext.Provider
      value={{
        id,
        error,
        disabled,
        readOnly,
        validationId,
        labelId,
        hasValidation
      }}
    >
      <div
        className={cx(styles.InputFieldRoot, className)}
        data-disabled={disabled}
        data-error={error}
        data-readonly={readOnly}
        role="group"
      >
        {children}
      </div>
    </InputFieldContext.Provider>
  )
}

const Container = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    const context = useContext(InputFieldContext)

    if (!context)
      throw new Error(
        'InputField.Container must be used within InputField.Root'
      )

    return (
      <div
        aria-labelledby={context.labelId}
        className={cx(
          styles.InputFieldContainer,
          context.error && styles.InputFieldContainerError,
          context.disabled && styles.InputFieldContainerDisabled,
          context.readOnly && styles.InputFieldContainerReadOnly,
          className
        )}
        data-disabled={context.disabled}
        data-error={context.error}
        data-readonly={context.readOnly}
        ref={ref}
        {...props}
      />
    )
  }
)

const Label = forwardRef<
  HTMLLabelElement,
  ComponentPropsWithoutRef<'label'> & { visuallyHidden?: boolean }
>(({ className, visuallyHidden, ...props }, ref) => {
  const context = useContext(InputFieldContext)

  if (!context)
    throw new Error('InputField.Label must be used within InputField.Root')

  return (
    <label
      className={cx(styles.InputFieldLabel, className)}
      data-visually-hidden={visuallyHidden}
      htmlFor={context.id}
      id={context.labelId}
      ref={ref}
      {...props}
    />
  )
})

type ValidationVariant = 'error' | 'success' | 'default'

const Validation = forwardRef<
  HTMLSpanElement,
  { children: ReactNode; className?: string; variant?: ValidationVariant }
>(({ className, children, variant = 'error', ...props }, ref) => {
  const context = useContext(InputFieldContext)

  if (!context)
    throw new Error('InputField.Validation must be used within InputField.Root')

  return (
    <SharedValidation
      className={className}
      id={context.validationId}
      ref={ref}
      variant={variant}
      {...props}
    >
      {children}
    </SharedValidation>
  )
})

const HelperText = forwardRef<
  HTMLSpanElement,
  { children: ReactNode; className?: string }
>(({ className, children, ...props }, ref) => {
  const context = useContext(InputFieldContext)

  if (!context)
    throw new Error('InputField.HelperText must be used within InputField.Root')

  return (
    <SharedValidation
      aria-live="polite"
      className={cx(styles.InputFieldHelperText, className)}
      id={`${context.id}-helper`}
      ref={ref}
      role="status"
      variant="default"
      {...props}
    >
      {children}
    </SharedValidation>
  )
})

const AffixContainer = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ className, children, ...props }, ref) => {
  return (
    <div
      className={cx(styles.InputFieldAffixContainer, className)}
      ref={ref}
      {...props}
    >
      {children}
    </div>
  )
})

const Prefix = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<'span'>>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        className={cx(styles.InputFieldPrefix, className)}
        ref={ref}
        {...props}
      >
        {children}
      </span>
    )
  }
)

const Suffix = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<'span'>>(
  ({ className, children, ...props }, ref) => {
    return (
      <span
        className={cx(styles.InputFieldSuffix, className)}
        ref={ref}
        {...props}
      >
        {children}
      </span>
    )
  }
)

Root.displayName = 'InputField.Root'
Container.displayName = 'InputField.Container'
Label.displayName = 'InputField.Label'
Validation.displayName = 'InputField.Validation'
HelperText.displayName = 'InputField.HelperText'
AffixContainer.displayName = 'InputField.AffixContainer'
Prefix.displayName = 'InputField.Prefix'
Suffix.displayName = 'InputField.Suffix'

export const InputField = {
  Root,
  Container,
  Label,
  Validation,
  HelperText,
  AffixContainer,
  Prefix,
  Suffix
}
